import * as React from 'react';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../consts/report';
import {
  getReportDefaultGendersForMultiSelect,
  getReportAgesForMultiSelect
} from '../../../../../../helpers/multiselect/multiselect';
import { School } from '../../../../../../models/school';
import { SchoolForm } from '../../../../../../models/form';
import { getStartDateForSchoolYear } from '../../../../../../helpers/calendar/calendar';
import '../SchoolEvents.scss';
import {
  ReportActions,
  ReportCheckboxGrid,
  ReportDateRange,
  ReportDescription,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from '../SchoolEventView/Reports/ReportFormUi';

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  school: School;
  forms: SchoolForm[];
}

const getAcademicYear = (date: Date) => (date.getMonth() >= 8 ? date.getFullYear() : date.getFullYear() - 1);

// report 067
export function FixtureStatisticsForPeriodReport({ onCloseClick, onSubmit, school, forms }: Props) {
  const now = new Date();
  let dateFrom = getStartDateForSchoolYear(now);
  const genders = getReportDefaultGendersForMultiSelect();
  const ages = getReportAgesForMultiSelect(school, forms);

  const [isAgesAndGendersChecked, setIsAgesAndGendersChecked] = useState(true);

  const requestInitial = {
    dateFrom,
    dateTo: now,
    isShowInterSchool: true,
    isShowHouses: true,
    isShowIntraSchool: true,
    isShowTournament: true,
    genders,
    ages,
    isSelectAllAges: true,
    isPrintPP: false,
    isPrintSEN: false,
    isPrintFSM: false,
    isPrintEthnicity: false
  };

  const onGendersChange = (index, values, setFieldValue) => {
    const genderItems = values.genders;
    const ageItems = values.ages;

    const genderItemsUpdated = genderItems.map((genderItem, genderIndex) => {
      return genderIndex === index
        ? {
            ...genderItem,
            isChecked: !genderItem.isChecked
          }
        : genderItem;
    });

    const gendersChecked = genderItemsUpdated.map(gender => gender.isChecked);
    const agesChecked = ageItems.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedGendersExist && isCheckedAgesExist);
    setFieldValue('genders', genderItemsUpdated);
  };

  const onAgesChange = (index, values, setFieldValue) => {
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map((ageItem, ageIndex) => {
      return ageIndex === index
        ? {
            ...ageItem,
            isChecked: !ageItem.isChecked
          }
        : ageItem;
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    const isSelectAllAges = ageItemsUpdated.every(age => age.isChecked === true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  const onSelectAllAgesChange = (event, values, setFieldValue) => {
    const isSelectAllAges = event.target.checked;
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map(age => {
      return {
        ...age,
        isChecked: isSelectAllAges
      };
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  return (
    <Formik
      onSubmit={values => onSubmit(values, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.FIXTURE_STATISTICS_FOR_PERIOD)}
      initialValues={requestInitial}
    >
      {({ values, setFieldValue }) => {
        const hasFixtureType =
          values.isShowInterSchool || values.isShowHouses || values.isShowIntraSchool || values.isShowTournament;
        const isHistoricalPeriod = getAcademicYear(values.dateFrom) !== getAcademicYear(new Date());
        const status = !hasFixtureType
          ? 'Select at least one fixture type.'
          : !isAgesAndGendersChecked
          ? 'Select at least one gender and one age group.'
          : isHistoricalPeriod &&
            (values.isPrintPP || values.isPrintSEN || values.isPrintFSM || values.isPrintEthnicity)
          ? 'Student breakdowns use the values currently recorded on each student profile.'
          : 'Ready to generate';

        return (
          <Form className="eReportForm">
            <ReportDescription>
              Review fixture and unique student participation statistics for a selected period, organised by sport.
            </ReportDescription>
            <ReportSection number={1} title="Reporting period" hint="Choose the date range covered by the report.">
              <ReportDateRange
                dateFrom={values.dateFrom}
                dateTo={values.dateTo}
                onChangeFrom={date => {
                  setFieldValue('dateFrom', date);
                  if (values.dateTo < date) setFieldValue('dateTo', date);
                }}
                onChangeTo={date => setFieldValue('dateTo', date)}
              />
            </ReportSection>
            <ReportSection number={2} title="Fixture types" hint="Choose which kinds of fixtures to include.">
              <ReportToggleList>
                <ReportToggle
                  name="isShowInterSchool"
                  label="Inter-school fixtures"
                  hint="Fixtures against another school."
                  value={values.isShowInterSchool}
                  onChange={value => setFieldValue('isShowInterSchool', value)}
                />
                <ReportToggle
                  name="isShowHouses"
                  label="House fixtures"
                  hint="Internal fixtures between houses."
                  value={values.isShowHouses}
                  onChange={value => setFieldValue('isShowHouses', value)}
                />
                <ReportToggle
                  name="isShowIntraSchool"
                  label="Internal fixtures"
                  hint="Other internal school fixtures."
                  value={values.isShowIntraSchool}
                  onChange={value => setFieldValue('isShowIntraSchool', value)}
                />
                <ReportToggle
                  name="isShowTournament"
                  label="Tournament events"
                  hint="Games from tournaments your school participated in."
                  value={values.isShowTournament}
                  onChange={value => setFieldValue('isShowTournament', value)}
                />
              </ReportToggleList>
            </ReportSection>
            <ReportSection number={3} title="Gender" hint="Select at least one gender category.">
              <ReportCheckboxGrid
                items={values.genders}
                onChange={index => onGendersChange(index, values, setFieldValue)}
              />
            </ReportSection>
            <ReportSection number={4} title="Age groups" hint="Select the age groups included in the report.">
              <ReportToggleList>
                <ReportToggle
                  name="isSelectAllAges"
                  label="Select all ages"
                  hint="Include every available age group."
                  value={values.isSelectAllAges}
                  onChange={value => onSelectAllAgesChange({ target: { checked: value } }, values, setFieldValue)}
                />
              </ReportToggleList>
              <ReportCheckboxGrid items={values.ages} onChange={index => onAgesChange(index, values, setFieldValue)} />
            </ReportSection>
            <ReportSection number={5} title="Student details" hint="Choose additional student breakdowns to include.">
              <ReportToggleList>
                <ReportToggle
                  name="isPrintPP"
                  label="Show PP"
                  hint="Include PP information."
                  value={values.isPrintPP}
                  onChange={value => setFieldValue('isPrintPP', value)}
                />
                <ReportToggle
                  name="isPrintSEN"
                  label="Show SEN"
                  hint="Include SEN information."
                  value={values.isPrintSEN}
                  onChange={value => setFieldValue('isPrintSEN', value)}
                />
                <ReportToggle
                  name="isPrintFSM"
                  label="Show FSM"
                  hint="Include FSM information."
                  value={values.isPrintFSM}
                  onChange={value => setFieldValue('isPrintFSM', value)}
                />
                <ReportToggle
                  name="isPrintEthnicity"
                  label="Break down by ethnicity codes"
                  hint="Include an ethnicity-code breakdown."
                  value={values.isPrintEthnicity}
                  onChange={value => setFieldValue('isPrintEthnicity', value)}
                />
              </ReportToggleList>
            </ReportSection>
            <ReportActions
              onCancel={onCloseClick}
              disabled={!isAgesAndGendersChecked || !hasFixtureType}
              status={status}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
